import React, { useState, useEffect } from "react";
import FormModal from "./modifyModal";
import { callAPI, hasRoles } from "../../utils";

const generateSessionLengthOptions = () =>
  Array.from({ length: 20 }, (_, index) => {
    const minutes = (index + 1) * 15;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return { value: minutes, label: `${hours ? `${hours}h ` : ""}${remainingMinutes}m` };
  });

const generateRatingOptions = (start = 1, end = 10) => 
  Array.from({ length: end - start + 1 }, (_, i) => ({
    value: i + start,
    label: (i + start).toString()
  }));

// Mapping function to transform session data
const mapSessionData = (mapData) => {
  return {
    tutor: {
      name: { value: mapData.tutorName?.value || null },
      id: { value: mapData.tutorId?.value || null },
      level: { value: mapData.tutorLevel?.value || null },
      notes: { value: mapData.notes?.value || null },
    },
    student: {
      name: { value: mapData.studentName?.value || null },
      id: { value: mapData.studentId?.value || null },
      subject: { value: mapData.subjectId?.value || null },
    },
    session: {
      date: { value: mapData.date?.value || null },
      MoD: { value: mapData.methodOfDelivery?.value || null },
      duration: { value: mapData.length?.value || null },
      rating: { value: mapData.rating?.value || null },
      feedback: { value: mapData.feedback?.value || null },
      status: { value: mapData.status?.value || null },
      submitedTime: { value: mapData.submittedTime?.value || null },
      sessionId: { value: mapData.sessionId?.value || null }
    },
  };
};

const ManageSessionModal = ({ data, callbacks, mode }) => {
  const [staticTables, setStaticTables] = useState([]);
  const [tutorOptions, setTutorOptions] = useState([]);
  
  const [studentOptions, setStudentOptions] = useState([]);
  const [allStudents, setAllStudents] = useState([]);

  const [subjectOptions, setSubjectOptions] = useState([]);
  const [allSubjects, setAllSubjects] = useState([]);

  const [initialFormData, setInitialFormData] = useState({});

  // State to track form values
  const [formValues, setFormValues] = useState({
    tutor: {
      id: null,
      level: null,
    },
    student: {
      id: null,
      subject: null,
    },
    session: {
      date: null,
      duration: null,
      rating: null,
      feedback: null,
      status: null,
    }
  });

  // Fetch data for tutors and students
  useEffect(() => {
    const fetchTutorOptions = async () => {
      try {
        let tutorList = []
        if (hasRoles("Admin")){
          const response = await callAPI({
            request: "database/admin/getEntities",
            method: "post",
            params: { entityType: "tutors", fields: ["id", "name", "level", "students"] },
          });
  
          const tutors = response.data.data;
          tutorList = tutors.map((tutor) => ({
            label: tutor.name.value,
            value: tutor.id.value,
            level: tutor.level.value,
            students: tutor.students.value 
              ? tutor.students.value.split(',').map(studentId => parseInt(studentId.trim())) 
              : []
          }));
        } else {
          const response = await callAPI({
            request: "getUsers",
            method: "get",
            params: { self: true },
          });
  
          const tutor = response.data;

          tutorList = [{
            label: tutor.name,
            value: tutor.tutorId,
            level: tutor.level,
            students: tutor.students 
              ? tutor.students.split(',').map(studentId => parseInt(studentId.trim())) 
              : []
          }];

          const updatedFormValues = {
            ...formValues,
            tutor: { 
              ...formValues.tutor,
              id: {value: tutor.tutorId},
              level: {value: tutor.level},
              name: {value: tutor.name}
            }
          };
          
          setFormValues(updatedFormValues);
        }
        setTutorOptions(tutorList);
      } catch (error) {
        console.error("Error fetching tutors:", error);
      }
    };

    const fetchStudentOptions = async () => {
      if (hasRoles("Admin")) {
        await callAPI({
          request: "database/admin/getEntities",
          method: "post",
          params: { entityType: "students", tables: ["name", "id", "subjects"] },
          onSuccess: (response) => {
            const data = response.data.data
            const studentList = data.map((student) => ({ 
              label: student.name.value, 
              value: student.id.value,
              subjects: student.subjects
            }));

            console.log(studentList)

            setStudentOptions(studentList);
            setAllStudents(studentList)
          },
          onError: (errorMessage) => {
            console.error("Failed to fetch students:", errorMessage);
          },
        });
      } else {
        await callAPI({
          request: "database/tutor/getMyStudents",
          method: "get",
          onSuccess: (response) => {
            const data = response.data.data;
            const studentList = data.map((student) => ({ 
              label: student.name.value, 
              value: student.studentId.value,
              subjects: student.subjects
                ? student.subjects
                : []
            }));
            console.log(studentList)

            setStudentOptions(studentList);
            setAllStudents(studentList)
          },
          onError: (errorMessage) => {
            console.error("Failed to fetch students:", errorMessage);
          },
        });
      }
    };

    const getStaticTables = async () => {
      await callAPI({
        request: "database/getStaticTables",
        method: "post",
        params: { tables: fieldsConfig.staticTables || [] },
        onSuccess: (response) => {
          setStaticTables(response.data);

          setAllSubjects(response.data.subjects)
        },
        onError: (errorMessage) => {
          console.error("Failed to fetch static tables:", errorMessage);
        },
      });
    };

    fetchTutorOptions();
    fetchStudentOptions();
    getStaticTables()
  }, []);

  const handleFieldChange = (change) => {
    const value = change.target.value;
    const fieldId = change.target.id

    const [section, field] = (fieldId || '').split('.');
    const updatedFormValues = { 
      ...formValues, 
      [section]: {
          ...formValues[section],
          [field]: value 
      }
    };

    if (fieldId === "tutor.id") {
      const selectedTutor = tutorOptions.find((tutor) => tutor?.value === value?.value);
      if (selectedTutor){
        updatedFormValues.tutor.level = {value: selectedTutor.level};

        const selectedStudents = allStudents.filter(student => 
          selectedTutor.students.includes(student.value)
        );
        
        updatedFormValues.student.id = {value: null}
        setStudentOptions(selectedStudents)
      }  
      
      if (!value) {
        updatedFormValues.student.id = {value: null}
        setStudentOptions([])
      }
    }

    if (fieldId === "student.id") {
      const selectedStudent = studentOptions.find((student) => student?.value === value?.value);
      if (selectedStudent){
        const subjects = allSubjects.filter(subject => 
          selectedStudent.subjects.includes(subject.value)
        );
        
        updatedFormValues.student.subject = {value: null}
        setSubjectOptions(subjects)
      }
      
      if (!value) {
        updatedFormValues.student.subject = {value: null}
        setSubjectOptions([])
      }
        
    }
    setFormValues(updatedFormValues);
  };

  const getCurrentSubjects = () => {
    const selectedStudent = studentOptions.find((student) => student?.value === formValues?.student?.id?.value);
    if (selectedStudent){

      const subjects = selectedStudent.subjects
      return subjects;
    }
    return [];
  }

  const studentSubjects = getCurrentSubjects();
  const isSubjectsAvailable = studentSubjects && studentSubjects.length > 0;

  const fieldsConfig = {
    mapData:mapSessionData,
    staticTables: ["tutorlevels", "methodofdelivery", "subjects", "sessionstatus"],
    entityType: "students",
    sections: [
      {
        section: "Tutor",
        fields: [
          {
            label: "Tutor Name",
            id: "tutor.id",
            type: "select",
            required: true,
            options: tutorOptions,
            value: formValues.tutor.id,
            disabled: !hasRoles("Admin"),
            onChange: (value) => handleFieldChange(value),
          },
          {
            label: "Tutor Level",
            id: "tutor.level",
            type: "select",
            required: true,
            options: staticTables.level || [],
            value: formValues.tutor.level,
            disabled: !hasRoles("Admin"),
          },
        ],
      },
      {
        section: "Student",
        fields: [
          {
            label: (studentOptions && studentOptions.length > 0) ? "Student Name": "No students found for tutor" ,
            id: "student.id",
            type: "select",
            required: true,
            options: studentOptions,
            value: formValues.student.id,
            disabled: !(studentOptions && studentOptions.length > 0), 
            onChange: (value) => handleFieldChange(value),
          },
          {
            label: "Subject",
            id: "student.subject",
            type: "select",
            required: true,
            options: studentSubjects,
            value: formValues.student.subject,
            disabled: !isSubjectsAvailable, 
          },
        ],
      },
      {
        section: "Session",
        fields: [
          { label: "Method of Delivery", id: "session.MoD", type: "select", required: true, value: formValues.session.MoD, options: staticTables?.methodofdelivery.filter((option) => option.label !== "Hybrid")},
          { label: "Session Date", id: "session.date", type: "date", required: true, value: formValues.session.date },
          { label: "Duration", id: "session.duration", type: "select", required: true, options: generateSessionLengthOptions(), value: formValues.session.duration },
          { label: "Rating", id: "session.rating", type: "select", required: true, options: generateRatingOptions(1, 10), value: formValues.session.rating },
          { label: "Feedback", id: "session.feedback", type: "textarea", value: formValues.session.feedback },
        ],
      },
    ],
  };

  useEffect(() => {
    const getFormData = async () => {
      if ((mode === "edit" || mode === "editWithName")) {
        await callAPI({
          request: "sessions/getSession", 
          method: "get",
          params: { sessionId: data.sessionId },
          onSuccess: (response) => {
              const mappedData = fieldsConfig.mapData(response.data.data[0]);
              setFormValues(mappedData);
              setInitialFormData(mappedData);
          },
          onError: (errorMessage) => {
              console.error("Failed to fetch data:", errorMessage);
          },
        });
      }
    };

      getFormData();
  }, [data.sessionId, mode]);

  const submitAPI = mode === "create" ? "sessions/createSession": "sessions/editSession";
  const deleteAPI = `sessions/deleteSession/${data.sessionId}`;

  const onSubmitSuccess = (updatedData, mode) => {
    callbacks.updateRow(updatedData, "tutorId", mode === "editWithName" ? "modify" : "create");
    callbacks.onClose();
    if (data?.reloadOnSuccess) window.location.reload();
  };

  return (
    <FormModal
      fieldsConfig={fieldsConfig}
      data={data}
      mode={mode}
      changes = {"default"}
      type={mode === "editWithName" ? "sidePeak" : "popup"}
      title={mode === "editWithName" ? "Editing session for: " + formValues?.["student"]?.["name"]?.value : "Creating Session"}
      onSubmitSuccess={onSubmitSuccess}
      onClose={callbacks.onClose}
      submitAPI={submitAPI}
      deleteAPI={deleteAPI}
      formData={formValues}
      initialFormData = {initialFormData}
      setFormData={setFormValues}
    />
  );
};

export default ManageSessionModal;
